<template>
  <v-app>
    <v-container fluid>
      <v-card variant="outlined">
        <v-card-title class="d-flex flex-wrap align-center ga-2 bg-blue-grey-lighten-4 text-subtitle-1">
          <span>Production Raw-Material Requisitions</span>
          <v-spacer />
          <v-text-field v-model="search" label="Search" prepend-inner-icon="mdi-magnify" density="compact" variant="outlined" hide-details clearable style="max-width: 280px" />
          <v-btn color="primary" prepend-icon="mdi-plus" :to="{ name: 'create-requisition' }">Create Requisition</v-btn>
        </v-card-title>
        <v-data-table :headers="headers" :items="items" :search="search" :loading="loading" density="compact">
          <template #item.index="{ index }">{{ index + 1 }}</template>
          <template #item.source="{ item }">{{ item.type === 'sale_order' ? item.sale_order?.code || 'Sales Order' : 'Bulk' }}</template>
          <template #item.outputs="{ item }">
            <div v-for="detail in item.details || []" :key="detail.id" class="text-caption">
              {{ detail.product?.name }} · {{ number(detail.quantity) }} · {{ detail.template?.name || '-' }} v{{ detail.template_version_no || 1 }}
            </div>
          </template>
          <template #item.workflow_status="{ item }">
            <v-chip size="small" color="indigo" variant="tonal">{{ labelize(item.workflow_status) }}</v-chip>
          </template>
          <template #item.approval_status="{ item }">
            <v-chip size="small" :color="approvalColor(item.approval_status)">{{ item.approval_status || (item.status ? 'Pending' : 'Draft') }}</v-chip>
          </template>
          <template #item.actions="{ item }">
            <v-btn icon="mdi-file-document-outline" color="blue" variant="text" size="small" :to="{ name: 'production-requisition-details', params: { id: item.id } }" />
            <v-btn v-if="canEdit(item) && $updateAccess($route.path)" icon="mdi-pencil-outline" color="primary" variant="text" size="small" :to="{ name: 'create-requisition', query: { id: item.id } }" />
          </template>
        </v-data-table>
      </v-card>
    </v-container>
  </v-app>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

const store = useStore();
const search = ref("");
const loading = ref(false);
const items = computed(() => store.getters.getRawMetarialRequisitionList || []);
const headers = [
  { title: "#", key: "index", sortable: false },
  { title: "Code", key: "code" },
  { title: "Date", key: "requisition_date" },
  { title: "Source", key: "source" },
  { title: "Finished Product / Qty / Template", key: "outputs", sortable: false },
  { title: "Workflow", key: "workflow_status" },
  { title: "Approval", key: "approval_status" },
  { title: "Actions", key: "actions", sortable: false },
];

function number(value) {
  return Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function labelize(value) {
  return String(value || "Draft").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function approvalColor(status) {
  if (status === "Approved") return "success";
  if (status === "Cancelled" || status === "Rejected") return "error";
  return "warning";
}

function canEdit(item) {
  return !(item.deliveries || []).length && !item.approval_current_process && (!item.approval_status || item.approval_status === "Pending") && Number(item.status) === 0;
}

onMounted(async () => {
  loading.value = true;
  try {
    await store.dispatch("getRawMetarialRequisitionList");
  } catch (error) {
    toast.error(error?.response?.data?.message || "Production requisition list could not be loaded.");
  } finally {
    loading.value = false;
  }
});
</script>
